// Finance ▸ Open items (CEO 2026-10-09) — the QuickBooks clean-up tracker.
//
// The 16 open items from the handoff and the decisions still to make, editable
// by Jake and Stephanie with every change kept. Items with a live test show
// green/red straight from QuickBooks when it's connected; the rest are checked
// by a person.
import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, ApiError, apiErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";
import { FinanceHeader } from "./FinanceNav";
import { CheckBadge, focusRing, shortDate, type CheckResult, type LiveFinance } from "./shared";

type Status = "open" | "in_progress" | "done";
interface Item {
  id: string;
  kind: "item" | "decision";
  number: number;
  title: string;
  sourceDoc: string;
  lastKnownStatus: string;
  verify: string;
  owner: string;
  status: Status;
  notes: string;
  checkKey: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}
interface History { changedAt: string; changedByName: string | null; changes: Record<string, { from: string; to: string }> }

const STATUS: Record<Status, [string, string]> = {
  open: ["Open", "bg-warn-bg text-warn"],
  in_progress: ["In progress", "bg-paper text-ink"],
  done: ["Done", "bg-ok-bg text-ok"],
};
const FIELD_WORD: Record<string, string> = { status: "Status", owner: "Owner", notes: "Notes", lastKnownStatus: "Last known status" };

export function OpenItemsPage() {
  const [items, setItems] = useState<Item[] | null>(null);
  const [ready, setReady] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checks, setChecks] = useState<Record<string, CheckResult> | null>(null);
  const [liveState, setLiveState] = useState<"loading" | "on" | "off">("loading");
  const [filter, setFilter] = useState<"todo" | "all">("todo");
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api<{ ready: boolean; items: Item[] }>("/api/finance/open-items");
      setReady(r.ready);
      setItems(r.items);
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't load the open items."));
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    api<LiveFinance>("/api/finance/live")
      .then((d) => { setChecks(d.checks ?? null); setLiveState(d.connected ? "on" : "off"); })
      .catch(() => setLiveState("off"));
  }, []);

  const replace = (next: Item) => setItems((xs) => xs?.map((x) => (x.id === next.id ? next : x)) ?? null);
  const all = items ?? [];
  // "Still to do" keeps a Done item on screen while its live test is red.
  const red = (i: Item) => !!i.checkKey && checks?.[i.checkKey]?.state === "fail";
  const list = (kind: Item["kind"]) => all.filter((i) => i.kind === kind && (filter === "all" || i.status !== "done" || red(i)));
  const needsWork = all.filter((i) => i.kind === "item" && red(i)).length;
  const count = (s: Status) => all.filter((i) => i.kind === "item" && i.status === s).length;

  return (
    <div className="mx-auto w-full max-w-[1180px] px-6 py-bay md:px-10">
      <FinanceHeader
        title="Open items"
        intro="What's left from the QuickBooks clean-up, and the calls still to make. Items with a live test turn green or red from QuickBooks; the rest are checked by a person. Every change is kept."
      />

      {!ready ? (
        <p className="mt-gutter rounded-lg border border-rule bg-paper px-5 py-4 text-body">
          <b>Preview.</b> This is the starting list. Editing switches on once the tracker is set up on the server (one command Jake runs).
        </p>
      ) : null}
      {error ? (
        <div role="alert" className="mt-gutter flex items-center gap-4">
          <p className="text-body text-risk">{error}</p>
          <button type="button" className="btn-secondary" onClick={() => void load()}>Try again</button>
        </div>
      ) : null}
      {!items && !error ? <p className="mt-gutter flex items-center gap-2 text-body text-ink-muted"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…</p> : null}

      {items ? (
        <>
          <div className="mt-gutter flex flex-wrap items-center justify-between gap-3">
            <p className="text-label">
              <b>{count("open")}</b> open · <b>{count("in_progress")}</b> in progress · <b>{count("done")}</b> done
              {checks ? <> · <b className={cn(needsWork > 0 && "text-risk")}>{needsWork}</b> need work in QuickBooks</> : null}
              <span className="ml-3 text-meta text-ink-muted">
                {liveState === "loading" ? "Checking QuickBooks…" : liveState === "on" ? "Live tests: on" : "Live tests: off until QuickBooks is connected"}
              </span>
            </p>
            <div className="flex gap-4" role="group" aria-label="Show">
              {([["todo", "Still to do"], ["all", "Everything"]] as const).map(([k, label]) => (
                <button key={k} type="button" aria-pressed={filter === k} onClick={() => setFilter(k)} className={cn("border-b-2 pb-1 text-label font-semibold", focusRing, filter === k ? "border-ink" : "border-transparent text-ink-muted hover:text-ink")}>{label}</button>
              ))}
            </div>
          </div>

          <ItemTable caption="Open items" rows={list("item")} checks={checks} ready={ready} openId={openId} setOpenId={setOpenId} onSaved={replace} onConflict={load} />

          <h2 className="mt-bay text-[18px] font-semibold">Decisions to make</h2>
          <p className="mt-1 max-w-[68ch] text-body text-ink-muted">Places the June documents disagree. Settle each one, write the answer in Notes, mark it done.</p>
          <ItemTable caption="Decisions to make" rows={list("decision")} checks={null} ready={ready} openId={openId} setOpenId={setOpenId} onSaved={replace} onConflict={load} />
        </>
      ) : null}
    </div>
  );
}

function ItemTable(props: {
  caption: string;
  rows: Item[];
  checks: Record<string, CheckResult> | null;
  ready: boolean;
  openId: string | null;
  setOpenId: (id: string | null) => void;
  onSaved: (i: Item) => void;
  onConflict: () => void;
}) {
  if (!props.rows.length) return <p className="mt-4 text-body text-ink-muted">Nothing left here.</p>;
  return (
    <div className="mt-4 overflow-x-auto rounded-lg border border-rule">
      <table className="w-full min-w-[880px] text-left text-meta">
        <caption className="sr-only">{props.caption}</caption>
        <thead className="bg-paper text-ink-muted">
          <tr>
            <th scope="col" className="w-10 px-4 py-3 font-semibold">#</th>
            <th scope="col" className="px-4 py-3 font-semibold">Item</th>
            <th scope="col" className="w-[30%] px-4 py-3 font-semibold">Where it stands</th>
            <th scope="col" className="px-4 py-3 font-semibold">Owner</th>
            <th scope="col" className="px-4 py-3 font-semibold">Status</th>
            <th scope="col" className="px-4 py-3 font-semibold"><span className="sr-only">Edit</span></th>
          </tr>
        </thead>
        <tbody>
          {props.rows.map((r) => {
            const check = r.checkKey ? props.checks?.[r.checkKey] : undefined;
            const open = props.openId === r.id;
            return (
              <FragmentRow key={r.id}>
                <tr className="border-t border-rule align-top">
                  <td className="px-4 py-3 tabular-nums text-ink-muted">{r.number}</td>
                  <th scope="row" className="px-4 py-3 text-left font-normal">
                    <span className="block text-label font-semibold">{r.title}</span>
                    <span className="block text-ink-muted">{r.sourceDoc}{r.verify && r.verify !== "—" ? ` · Verify: ${r.verify}` : ""}</span>
                  </th>
                  <td className="px-4 py-3">
                    {check ? (
                      <span className="block"><CheckBadge check={check} /> <span className="text-ink">{check.summary}</span></span>
                    ) : r.checkKey && !props.checks ? (
                      <span className="block text-ink-muted">Live test waits for QuickBooks.</span>
                    ) : null}
                    <span className={cn("block", check && "mt-1 text-ink-muted")}>{r.lastKnownStatus}</span>
                    {r.notes ? <span className="mt-1 block whitespace-pre-line text-ink">Note: {r.notes}</span> : null}
                  </td>
                  <td className="px-4 py-3">{r.owner}</td>
                  <td className="px-4 py-3">
                    <span className={cn("inline-block whitespace-nowrap rounded px-1.5 font-semibold", STATUS[r.status][1])}>{STATUS[r.status][0]}</span>
                    {r.updatedAt ? <span className="mt-1 block text-ink-muted">{r.updatedByName ?? "—"}, {shortDate(r.updatedAt)}</span> : null}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {props.ready ? (
                      <button type="button" aria-expanded={open} onClick={() => props.setOpenId(open ? null : r.id)} className={cn("btn-secondary text-meta", focusRing)}>
                        {open ? "Close" : "Edit"}
                      </button>
                    ) : null}
                  </td>
                </tr>
                {open ? (
                  <tr className="border-t border-rule bg-paper">
                    <td />
                    <td colSpan={5} className="px-4 py-4">
                      <Editor item={r} onSaved={(i) => { props.onSaved(i); props.setOpenId(null); }} onConflict={props.onConflict} />
                    </td>
                  </tr>
                ) : null}
              </FragmentRow>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function FragmentRow({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function Editor({ item, onSaved, onConflict }: { item: Item; onSaved: (i: Item) => void; onConflict: () => void }) {
  const [status, setStatus] = useState<Status>(item.status);
  const [owner, setOwner] = useState(item.owner);
  const [lastKnown, setLastKnown] = useState(item.lastKnownStatus);
  const [notes, setNotes] = useState(item.notes);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<History[] | null>(null);
  useEffect(() => {
    api<{ history: History[] }>(`/api/finance/open-items/${item.id}/history`).then((r) => setHistory(r.history)).catch(() => setHistory([]));
  }, [item.id]);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ item: Item }>(`/api/finance/open-items/${item.id}`, {
        method: "PATCH",
        body: JSON.stringify({ expectedUpdatedAt: item.updatedAt, status, owner, lastKnownStatus: lastKnown, notes }),
      });
      onSaved(r.item);
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't save."));
      if (e instanceof ApiError && e.status === 409) onConflict();
    } finally {
      setBusy(false);
    }
  }

  const field = "mt-1 w-full rounded border border-rule bg-white px-3 py-2 text-label";
  return (
    <div className="grid gap-4 md:grid-cols-[1fr_1fr]">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-meta font-semibold">Status
            <select value={status} onChange={(e) => setStatus(e.target.value as Status)} className={cn(field, "min-h-[40px]", focusRing)}>
              <option value="open">Open</option>
              <option value="in_progress">In progress</option>
              <option value="done">Done</option>
            </select>
          </label>
          <label className="block text-meta font-semibold">Owner
            <input value={owner} onChange={(e) => setOwner(e.target.value)} maxLength={80} className={cn(field, "min-h-[40px]", focusRing)} />
          </label>
        </div>
        <label className="block text-meta font-semibold">Last known status
          <textarea value={lastKnown} onChange={(e) => setLastKnown(e.target.value)} rows={2} maxLength={1000} className={cn(field, focusRing)} />
        </label>
        <label className="block text-meta font-semibold">Notes
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={4000} placeholder="What you found in QuickBooks, what's left, the answer you settled on…" className={cn(field, focusRing)} />
        </label>
        <div className="flex items-center gap-4">
          <button type="button" className="btn-primary" disabled={busy} onClick={() => void save()}>{busy ? "Saving…" : "Save"}</button>
          {error ? <p role="alert" className="text-label text-risk">{error}</p> : null}
        </div>
      </div>
      <div>
        <p className="text-meta font-semibold">History</p>
        {history === null ? <p className="mt-1 text-meta text-ink-muted">Loading…</p> : history.length === 0 ? <p className="mt-1 text-meta text-ink-muted">No changes yet.</p> : (
          <ol className="mt-1 space-y-2">
            {history.map((h, i) => (
              <li key={i} className="text-meta">
                <span className="font-semibold">{h.changedByName ?? "Someone"}</span> <span className="text-ink-muted">· {shortDate(h.changedAt)}</span>
                {Object.entries(h.changes).map(([f, c]) => (
                  <span key={f} className="block text-ink-muted">{FIELD_WORD[f] ?? f}: {f === "status" ? `${STATUS[c.from as Status]?.[0] ?? c.from} → ${STATUS[c.to as Status]?.[0] ?? c.to}` : c.to || "(cleared)"}</span>
                ))}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
